// Filtros da Tela Atividade (§6.5 / wireframe seção 06).
//
// O wireframe pede Tipo = pipeline | skill | agent | companion | tool e
// Status = ok | em execução | falha. O backend (RPC get_activity_feed v4)
// expõe kinds = agent | skill | ephemeral | wizard | handler | companion —
// NÃO existe kind 'pipeline' nem 'tool' na API. Mapeamento adotado:
//
//   - pipeline → kind=handler no servidor + predicado client-side
//     (handler '@pipeline-runner' / origin 'pipeline-step')
//   - tool     → kind=handler no servidor + predicado client-side
//     (handler '@tool-runner' / origin 'companion-tool')
//   - ok → completed · em execução → running · falha → failed
//
// O predicado client-side também é aplicado sobre a lista visível para que
// inserts de realtime (que chegam sem filtro de kind/status) nunca vazem
// para um feed filtrado.

import type { ActivityItem, ActivityKind, ActivityStatus } from '@/lib/activity';

export type ActivityTypeFilter = 'all' | 'pipeline' | 'skill' | 'agent' | 'companion' | 'tool';
export type ActivityStatusFilter = 'all' | 'ok' | 'running' | 'failed';

export const TYPE_FILTER_OPTIONS: Array<{ value: ActivityTypeFilter; label: string }> = [
  { value: 'all', label: 'Tipo: todos' },
  { value: 'pipeline', label: 'pipeline' },
  { value: 'skill', label: 'skill' },
  { value: 'agent', label: 'agent' },
  { value: 'companion', label: 'companion' },
  { value: 'tool', label: 'tool' },
];

export const STATUS_FILTER_OPTIONS: Array<{ value: ActivityStatusFilter; label: string }> = [
  { value: 'all', label: 'Status: todos' },
  { value: 'ok', label: 'ok' },
  { value: 'running', label: 'em execução' },
  { value: 'failed', label: 'falha' },
];

/** Mapeia o filtro de Tipo do wireframe para o param `kind` da API. */
export function typeToKindParam(type: ActivityTypeFilter): 'all' | ActivityKind {
  switch (type) {
    case 'pipeline':
    case 'tool':
      return 'handler';
    case 'skill':
      return 'skill';
    case 'agent':
      return 'agent';
    case 'companion':
      return 'companion';
    case 'all':
      return 'all';
  }
}

/** Mapeia o filtro de Status do wireframe para o param `status` da API. */
export function statusToBackendParam(status: ActivityStatusFilter): 'all' | ActivityStatus {
  switch (status) {
    case 'ok':
      return 'completed';
    case 'running':
      return 'running';
    case 'failed':
      return 'failed';
    case 'all':
      return 'all';
  }
}

function isPipelineHandler(item: ActivityItem): boolean {
  if (item.kind !== 'handler') return false;
  const slug = (item.actor_slug ?? item.actor_name ?? '').toLowerCase();
  return slug.includes('pipeline') || item.invoker_type === 'pipeline-step';
}

/** Handler que executa tool (@tool-runner / companion-tool). Exportado: a
 *  ActivityFeedRow usa pra renderizar o lead `tool <code>target</code>`. */
export function isToolHandler(item: ActivityItem): boolean {
  if (item.kind !== 'handler') return false;
  const slug = (item.actor_slug ?? item.actor_name ?? '').toLowerCase();
  return slug.includes('tool') || item.invoker_type === 'companion-tool';
}

export function matchesType(item: ActivityItem, type: ActivityTypeFilter): boolean {
  switch (type) {
    case 'all':
      return true;
    case 'pipeline':
      return isPipelineHandler(item);
    case 'tool':
      return isToolHandler(item);
    case 'skill':
    case 'agent':
    case 'companion':
      return item.kind === type;
  }
}

export function matchesStatus(item: ActivityItem, status: ActivityStatusFilter): boolean {
  if (status === 'all') return true;
  return item.status === statusToBackendParam(status);
}

export function matchesSearch(item: ActivityItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item.actor_name,
    item.actor_slug,
    item.target_slug,
    item.parent_actor_name,
    item.parent_actor_slug,
    item.model_used,
    item.kind,
    item.id,
    item.task_id,
    item.trace_id,
    item.error_message,
  ];
  return haystack.some((v) => v != null && v.toLowerCase().includes(q));
}

export interface ActivityFeedFilterState {
  type: ActivityTypeFilter;
  status: ActivityStatusFilter;
  search: string;
}

/** Predicado completo aplicado sobre a lista visível (inclui rows de realtime). */
export function filterActivityFeed(
  items: ActivityItem[],
  filters: ActivityFeedFilterState,
): ActivityItem[] {
  return items.filter(
    (item) =>
      matchesType(item, filters.type) &&
      matchesStatus(item, filters.status) &&
      matchesSearch(item, filters.search),
  );
}
